import React, { useMemo, useState } from 'react';
import './ManualCMDB.css';

const MANUAL_SECTIONS = [
  {
    id: 'indice',
    chapter: 'Guía Completa',
    title: 'Índice del Manual de Despliegue CMDB',
    body: [
      '1. Requisitos, roles y credenciales',
      '2. Windows: instalación con instalador (setup.exe)',
      '3. Windows: instalación MSI por línea de comandos',
      '4. Windows: instalación MSI por directiva (GPO)',
      '5. Linux: instalación con install_linux.sh',
      '6. Actualización del agente (EXE, MSI y Linux)',
      '7. Qué es el "first scan" y cómo funciona',
      '8. El panel del agente y el servicio Windows',
      '9. Validación y troubleshooting',
      '10. Distribución de software con Chocolatey',
      '11. Inventario de antigüedad y licencia del sistema',
    ]
  },
  {
    id: 'requisitos',
    chapter: 'Capítulo 1',
    title: 'Requisitos, roles y credenciales',
    body: [
      'Artefactos de la versión 2.5.0: CMDBAgentSetup-2.5.0.exe (instalador NSIS), CMDBAgent-2.5.0.msi (WiX, para despliegue silencioso y GPO) y cmdb-agent-linux-2.5.0.zip (instalador Linux).',
      'URL del servidor: usa la URL base (por ejemplo https://cmdb.empresa.com). También se acepta el sufijo /api; el agente lo normaliza.',
      'Rol de despliegue (CMDB_AGENT_ROLE): host = inventaría el equipo donde se instala; network = recolector de red, no se auto-inventaría y solo ejecuta los ámbitos de descubrimiento configurados en CMDB (Agente → Red). Su heartbeat mantiene el equipo en verde, pero no lo reintroduce en el inventario.',
      'Credencial: CMDB_COMPANY_API_KEY (API key de empresa). El backend la promociona a credencial individual del equipo en el primer registro.',
      'Tipo de CI forzado (CMDB_FORCE_CI_TYPE, opcional): si un equipo sale clasificado como máquina virtual y no lo es, añade CMDB_FORCE_CI_TYPE=server al .env (o ejecuta cmdb-agent self --ci-type server) y vuelve a escanear. La detección automática solo marca virt_vm cuando el fabricante/modelo del SMBIOS es una firma de hipervisor conocida (VMware, Hyper-V, VirtualBox, QEMU, Xen, Proxmox…).',
      'Bootstrap: solo en builds del EXE preconfigurados al compilar con CMDB_URL, CMDB_COMPANY_SLUG y CMDB_BOOTSTRAP_TOKEN; el agente se enrola solo y rota la credencial. El MSI actual no admite propiedades de bootstrap: usa API key.',
      'Windows: requiere administrador (instala el servicio CMDBAgent) y salida HTTPS al servidor CMDB.',
      'Linux: requiere Python 3 con venv y systemd; el instalador crea el usuario de sistema cmdbagent y el servicio cmdb-agent.'
    ]
  },
  {
    id: 'windows-instalador',
    chapter: 'Capítulo 2',
    title: 'Windows: instalación con instalador (setup.exe)',
    body: [
      'Interactivo: ejecuta CMDBAgentSetup-2.5.0.exe como administrador. Al finalizar se abre el panel del agente; en Configurar introduce la URL, la API key y el rol, pulsa Validar y Guardar. El servicio arranca automáticamente.',
      'Silencioso: el instalador NSIS acepta /S y el rol en tiempo de ejecución con /ROLE=host|network. Escribe .env, instala y arranca el servicio sin abrir el panel.',
      'Limitación importante: el EXE no acepta la API key por línea de comandos en runtime. Opciones: configurarla después con el panel, generar un instalador preconfigurado con bootstrap al compilar, o desplegar el MSI (siguiente capítulo).',
      'El instalador conserva .env y .agent_id en las reinstalaciones normales; solo un build con bootstrap reescribe .env y fuerza re-enrolamiento.'
    ],
    codeBlocks: [
      {
        label: 'Instalación silenciosa con rol',
        language: 'powershell',
        code: 'CMDBAgentSetup-2.5.0.exe /S /ROLE=network'
      },
      {
        label: 'Build preconfigurado con bootstrap (compilación)',
        language: 'powershell',
        code: 'makensis /DCMDB_URL="https://cmdb.empresa.com" /DCMDB_COMPANY_SLUG="empresa" /DCMDB_BOOTSTRAP_TOKEN="<token>" installer\\setup.nsi'
      }
    ],
    checklist: [
      'Ejecutado como administrador.',
      'Servicio CMDBAgent en estado Running.',
      'Configuración (URL, API key y rol) guardada desde el panel si no se usó bootstrap.'
    ]
  },
  {
    id: 'windows-msi-cli',
    chapter: 'Capítulo 3',
    title: 'Windows: instalación MSI por línea de comandos',
    body: [
      'Requisitos: CMDBAgent-2.5.0.msi y permisos de administrador. Las propiedades se pasan directamente en msiexec; el MST es opcional y solo hace falta para GPO o para reutilizar una configuración fija.',
      'Propiedades: CMDB_URL (URL base), CMDB_COMPANY_API_KEY (API key de empresa) y CMDB_AGENT_ROLE (host|network). En una instalación nueva, URL y clave son obligatorias.',
      'El MSI escribe .env, instala el servicio CMDBAgent y lo arranca. Si el paquete incluye CrystalDiskInfo, también configura CMDB_CRYSTALDISKINFO_PATH y CMDB_CRYSTALDISKINFO_TIMEOUT.',
      'Registra siempre un log (/L*v) para diagnosticar fallos de condiciones o de la acción de configuración.'
    ],
    codeBlocks: [
      {
        label: 'Instalación silenciosa (host)',
        language: 'powershell',
        code: 'msiexec /i "C:\\ruta\\CMDBAgent-2.5.0.msi" /qn /norestart /L*v "C:\\ProgramData\\CMDBAgentDeploy\\msi.log" CMDB_URL="https://cmdb.empresa.com" CMDB_COMPANY_API_KEY="<api-key>" CMDB_AGENT_ROLE="host"'
      },
      {
        label: 'Instalación silenciosa (recolector de red)',
        language: 'powershell',
        code: 'msiexec /i "C:\\ruta\\CMDBAgent-2.5.0.msi" /qn /norestart /L*v "C:\\ProgramData\\CMDBAgentDeploy\\msi.log" CMDB_URL="https://cmdb.empresa.com" CMDB_COMPANY_API_KEY="<api-key>" CMDB_AGENT_ROLE="network"'
      },
      {
        label: 'Helper de despliegue',
        language: 'powershell',
        code: '.\\installer\\wix\\deploy-silent.ps1 -MsiPath .\\installer\\CMDBAgent-2.5.0.msi -CmdbUrl "https://cmdb.empresa.com" -CompanyApiKey "<api-key>" -Role network'
      }
    ],
    checklist: [
      'msiexec devuelve código 0.',
      'Servicio CMDBAgent en Running.',
      'El log del MSI no contiene errores de condición (CMDB_URL / CMDB_COMPANY_API_KEY).'
    ]
  },
  {
    id: 'windows-msi-gpo',
    chapter: 'Capítulo 4',
    title: 'Windows: instalación MSI por directiva (GPO)',
    body: [
      '1. Copia el MSI a una ruta UNC accesible por los equipos (por ejemplo \\\\dominio\\netlogon\\CMDB\\CMDBAgent-2.5.0.msi).',
      '2. El MST solo hace falta si despliegas por GPO en modo Assigned: en ese modo no se pueden pasar propiedades por línea de comandos. Con script, SCCM o Intune, pasa CMDB_URL, CMDB_COMPANY_API_KEY y CMDB_AGENT_ROLE directamente.',
      '3. Programas para crear el MST (en el equipo de administración, nunca en el servidor): InstEd (gratis, instedit.com; recomendado), Orca (gratis, Windows SDK → MSI Tools), SuperOrca (gratis) o msitran.exe/WiX por línea de comandos.',
      '4. Con InstEd: File → Open → el MSI; Transform → New Transform; en la tabla Property añade una fila por valor: CMDB_URL=https://cmdb.empresa.com, CMDB_COMPANY_API_KEY=<api-key> y CMDB_AGENT_ROLE=host o network; Transform → Save Transform As → CMDBAgent.mst. Con Orca es igual, pero termina en Transform → Generate Transform.',
      '5. El MST lleva la API key en claro: guárdalo en una ruta protegida y no lo subas al repositorio. Si se filtra, revoca la clave en Agente → Credenciales.',
      '6. Abre GPMC (Group Policy Management Console).',
      '7. Ve a: Computer Configuration → Policies → Software Settings → Software installation.',
      '8. Click derecho → New → Package → selecciona el MSI por ruta UNC (modo Assigned).',
      '9. En la pestaña Modifications añade el MST.',
      '10. Aplica la GPO a la OU de los equipos y reinicia para que se aplique la instalación asignada.',
      'Alternativa sin MST: publica un script de arranque (Computer Configuration → Windows Settings → Scripts → Startup) que ejecute msiexec con las propiedades. Es equivalente y más fácil de auditar.'
    ],
    codeBlocks: [
      {
        label: 'Script de arranque alternativo al MST',
        language: 'powershell',
        code: 'msiexec /i "\\\\servidor\\netlogon\\CMDB\\CMDBAgent-2.5.0.msi" /qn /norestart CMDB_URL="https://cmdb.empresa.com" CMDB_COMPANY_API_KEY="<api-key>" CMDB_AGENT_ROLE="network"'
      }
    ],
    checklist: [
      'MSI (y MST, si se usa) alojados en ruta UNC accesible por los equipos destino.',
      'GPO vinculada a la OU de equipos, no de usuarios.',
      'Política "Always wait for the network at computer startup" habilitada para evitar fallos de red en el arranque.',
      'Reinicio aplicado y servicio CMDBAgent en Running.',
      'Primer inventario visible en CMDB.'
    ]
  },
  {
    id: 'linux',
    chapter: 'Capítulo 5',
    title: 'Linux: instalación con install_linux.sh',
    body: [
      'Requisitos: servidor Linux con systemd (Ubuntu/Debian, RHEL…), 1 vCPU, 1 GB de RAM y 8 GB de disco; python3 con venv, unzip y salida HTTPS al CMDB. Un colector necesita además UDP/161 hacia los equipos, así que conviene ponerlo en la VLAN de gestión.',
      'No sirve en pfSense ni en otros FreeBSD (no hay systemd) ni en contenedores sin systemd: para contenedor, usa la imagen Docker que viene en el propio ZIP.',
      'Descarga cmdb-agent-linux-2.5.0.zip, descomprímelo en el servidor y entra en la carpeta cmdb-agent. El instalador crea el usuario cmdbagent, copia el código a /opt/cmdb-agent, monta su venv con requirements.txt y escribe /opt/cmdb-agent/.env.',
      'Instalación silenciosa: --url, --api-key (o --slug y --token para bootstrap), --role host|network y --non-interactive. La URL es la base, sin /api. Con --role network no se inventaría el propio host: configura los ámbitos y credenciales en CMDB (Agente → Red) antes del primer barrido.',
      'Otras opciones: --install-dir (por defecto /opt/cmdb-agent), --user (por defecto cmdbagent), --force-reconfigure (sobrescribe .env y la clave promocionada), --skip-validate, --no-start y --no-auto-update.',
      'Comprobación: systemctl status cmdb-agent, systemctl list-timers cmdb-agent-update.timer y /opt/cmdb-agent/cmdb-agent.log. Para probar un equipo suelto antes de lanzar el ámbito: agent.py network --probe <IP> --dry-run.',
      'Actualización: el instalador deja activo el timer diario cmdb-agent-update.timer, que descarga el ZIP publicado en la CMDB, sustituye el código, refresca las dependencias del venv y reinicia el servicio. A mano: agent.py update + systemctl restart cmdb-agent; o reinstalar con el ZIP nuevo, que conserva .env y la identidad.',
      'Alternativa en contenedor: docker-compose.collector.yml, que arranca con CMDB_AGENT_ROLE=network y network_mode host.'
    ],
    codeBlocks: [
      {
        label: 'Preparar la máquina',
        language: 'bash',
        code: 'sudo apt update && sudo apt install -y unzip python3 python3-venv\ncd /tmp && unzip cmdb-agent-linux-2.5.0.zip && cd cmdb-agent\nchmod +x install_linux.sh'
      },
      {
        label: 'Instalar como colector de red',
        language: 'bash',
        code: 'sudo ./install_linux.sh --url https://cmdb.empresa.com --api-key "<api-key>" \\\n  --role network --non-interactive'
      },
      {
        label: 'Instalar inventariando este servidor',
        language: 'bash',
        code: 'sudo ./install_linux.sh --url https://cmdb.empresa.com --api-key "<api-key>" \\\n  --role host --non-interactive'
      },
      {
        label: 'Bootstrap con slug y token',
        language: 'bash',
        code: 'sudo ./install_linux.sh --url https://cmdb.empresa.com --slug empresa --token "<token>" \\\n  --role network --non-interactive'
      },
      {
        label: 'Comprobar servicio, timer y log',
        language: 'bash',
        code: 'systemctl status cmdb-agent --no-pager\nsystemctl list-timers cmdb-agent-update.timer\ntail -f /opt/cmdb-agent/cmdb-agent.log\n\n# probar un equipo con las credenciales del perfil\nsudo -u cmdbagent /opt/cmdb-agent/.venv/bin/python \\\n  /opt/cmdb-agent/agent.py network --probe 10.0.0.10 --dry-run'
      },
      {
        label: 'Actualizar',
        language: 'bash',
        code: '# forzar la comprobación ahora\nsudo /opt/cmdb-agent/.venv/bin/python /opt/cmdb-agent/agent.py update\nsudo systemctl restart cmdb-agent\n\n# o reinstalar con el ZIP nuevo (conserva .env y la identidad)\nsudo ./install_linux.sh --url https://cmdb.empresa.com --role network --non-interactive'
      }
    ],
    checklist: [
      'systemctl status cmdb-agent en estado active (running).',
      'systemctl list-timers cmdb-agent-update.timer muestra la próxima ejecución.',
      'Rol correcto en /opt/cmdb-agent/.env (CMDB_AGENT_ROLE).',
      'En rol network, ámbitos configurados en CMDB antes del primer barrido.',
      'El equipo alcanza el CMDB por HTTPS y los switches por UDP/161.'
    ]
  },
  {
    id: 'actualizacion',
    chapter: 'Capítulo 6',
    title: 'Actualización del agente (EXE, MSI y Linux)',
    body: [
      'Antes de actualizar: comprueba la versión actual en la ficha del equipo (agent_version). Las actualizaciones conservan la identidad (.agent_id) y la credencial promocionada salvo que se indique lo contrario.',
      'Actualización remota desde CMDB: publica el instalador en Agente → Ajustes → Publicar instalador. Después, en la ficha del equipo, menú de sincronización → Actualizar agente. Cada agente descarga el mismo formato con el que se instaló (MSI o EXE) y lo ejecuta en silencio; los Linux aplican el ZIP fuente y se reinician por systemd.',
      'La ficha no da la actualización por buena al encolarla: muestra «Actualizando…» y solo pasa a «Actualizado <versión>» cuando el agente vuelve a latir con la versión nueva. Si el artefacto publicado es igual o más antiguo que el instalado, no se reinstala nada y queda en «Agente al día» (para forzarlo, `update --force`).',
      'Windows con EXE: ejecuta el nuevo CMDBAgentSetup-<nueva>.exe como administrador o con /S. El instalador detiene y elimina el servicio, reemplaza los binarios, conserva .env y .agent_id y vuelve a arrancar el servicio. Atención: si ese EXE se compiló con bootstrap, borra .env y fuerza re-enrolamiento.',
      'Windows con MSI: msiexec /i CMDBAgent-<nueva>.msi /qn /norestart. Es un Major Upgrade (mismo UpgradeCode): no hace falta volver a pasar URL ni API key; se conservan .env, la clave promocionada y el rol. Si quieres cambiar el rol, añade CMDB_AGENT_ROLE=network|host.',
      'Windows con MSI por GPO: sustituye el MSI en la ruta UNC y actualiza la asignación o el script de arranque. Mantén el MST si lo usas; la GPO aplicará la nueva versión en el siguiente reinicio.',
      'Linux: vuelve a ejecutar install_linux.sh con los mismos flags. Conserva la CMDB_API_KEY ya promocionada salvo --force-reconfigure, reinstala dependencias, actualiza el servicio systemd y lo reinicia.',
      'Linux sin redesplegar: ejecuta el comando update del agente, que descarga el ZIP fuente publicado por el servidor, y reinicia el servicio.',
      'Validación: servicio en Running, versión nueva visible en la ficha y sin duplicar el CI.'
    ],
    codeBlocks: [
      {
        label: 'Actualización silenciosa con MSI (sin propiedades)',
        language: 'powershell',
        code: 'msiexec /i "C:\\ruta\\CMDBAgent-2.5.0.msi" /qn /norestart /L*v "C:\\ProgramData\\CMDBAgentDeploy\\msi-update.log"'
      },
      {
        label: 'Actualización Linux desde el propio agente',
        language: 'bash',
        code: 'sudo -u cmdbagent /opt/cmdb-agent/.venv/bin/python /opt/cmdb-agent/agent.py update\nsudo systemctl restart cmdb-agent'
      },
      {
        label: 'Reinstalación Linux con el instalador',
        language: 'bash',
        code: 'sudo ./install_linux.sh --url https://cmdb.empresa.com --api-key "<api-key>" --role network --non-interactive'
      }
    ],
    checklist: [
      'Versión nueva visible en CMDB (agent_version).',
      'Servicio Running/active después de la actualización.',
      'La credencial y el CI se mantienen (sin re-enrolamiento) en actualizaciones normales.'
    ]
  },
  {
    id: 'first-scan',
    chapter: 'Capítulo 7',
    title: '¿Qué es el "first scan"?',
    body: [
      'Es el primer inventario completo que realiza el agente tras instalarse y arrancar el servicio.',
      'Registra el equipo en CMDB, recoge hardware, software, red, etc.',
      'El backend promociona la credencial de empresa a una individual.',
      'El estado se guarda en first-scan-state.json (Windows: carpeta del agente).'
    ]
  },
  {
    id: 'panel-servicio',
    chapter: 'Capítulo 8',
    title: 'El panel del agente y el servicio Windows',
    body: [
      'Servicio: CMDBAgent (Windows Service) — Arranca automáticamente tras instalar y ejecuta el agente en modo daemon.',
      'Panel: Interfaz gráfica para ver estado, logs, forzar inventario y cambiar configuración (URL, API key, rol).',
      'Linux: el equivalente es el servicio systemd cmdb-agent; no hay panel gráfico, la configuración vive en /opt/cmdb-agent/.env.'
    ]
  },
  {
    id: 'validacion',
    chapter: 'Capítulo 9',
    title: 'Validación y troubleshooting',
    body: [
      'Windows — servicio: Get-Service CMDBAgent',
      'Windows — logs: C:\\Program Files\\CMDB Agent\\cmdb-agent.log',
      'Windows — estado del primer scan: C:\\Program Files\\CMDB Agent\\first-scan-state.json',
      'Windows — forzar inventario manual: & "C:\\Program Files\\CMDB Agent\\cmdb-agent.exe" self --yes',
      'Linux — servicio: systemctl status cmdb-agent',
      'Linux — logs: journalctl -u cmdb-agent -e',
      'Errores comunes:',
      '  - El MSI no instala: en instalación nueva faltan CMDB_URL o CMDB_COMPANY_API_KEY (revisa el log /L*v).',
      '  - No actualiza de forma remota en Windows: no hay instalador EXE publicado en Agente → Ajustes.',
      '  - El .env no se rellena: parámetros no pasados o permisos insuficientes.',
      '  - Rol incorrecto: revisa CMDB_AGENT_ROLE en .env (host|network).',
      '  - No hay primer inventario: conectividad, DNS, proxy o credencial inválida.'
    ]
  },
  {
    id: 'chocolatey',
    chapter: 'Capítulo 10',
    title: 'Distribución de software con Chocolatey',
    body: [
      'El agente 2.6.11 o superior puede instalar y actualizar software con Chocolatey sin entrar por RDP. El catálogo y los despliegues se gestionan desde la CMDB: Agente → Paquetes (global) o Ficha del ordenador → Software (un equipo).',
      'Flujo: se da de alta el id de Chocolatey (logioptionsplus, googlechrome, 7zip…), se elige el destino y la CMDB encola un comando install_package. El agente ejecuta choco upgrade <paquete> --install-if-not-installed en un hilo aparte y sigue latiendo mientras instala.',
      'Si el equipo no tiene Chocolatey, el agente lo instala antes con el script oficial de community.chocolatey.org. El equipo necesita salida a Internet o a un repositorio interno de Chocolatey.',
      'Un solo paquete a la vez por equipo: un segundo despliegue queda en error con un mensaje claro. Los instaladores con interfaz no funcionan bajo el servicio SYSTEM; Chocolatey sí.',
      'El resultado vuelve a la CMDB con la versión instalada o el error. Se puede reintentar desde el mismo panel.',
      'Aviso: los paquetes de la comunidad pueden estar por detrás de la versión que ya tiene el equipo. Un downgrade falla (exit 1287 en el instalador de Logitech). Para software propietario conviene internalizar el instalador en un repositorio propio.',
      'Diagnóstico: en la ficha del equipo, «Comprobar Chocolatey» encola un choco_status y devuelve ruta y versión.'
    ],
    checklist: [
      'Agente 2.6.11 o superior en el equipo.',
      'Salida a community.chocolatey.org o repositorio interno.',
      'El paquete aparece en el catálogo con su id exacto.'
    ]
  },
  {
    id: 'antiguedad-licencia',
    chapter: 'Capítulo 11',
    title: 'Inventario de antigüedad y licencia del sistema',
    body: [
      'El agente 2.6.11 envía la fecha de la BIOS (Win32_BIOS.ReleaseDate), su versión, la fecha de instalación del Windows actual (Win32_OperatingSystem.InstallDate), la última fecha de arranque y los datos de licencia del sistema.',
      'La licencia se lee de SoftwareLicensingProduct y SoftwareLicensingService: canal (OEM/Retail/Volume:MAK/KMS), los 5 últimos caracteres de la clave, el estado de activación y si hay clave OEM grabada en el firmware. La clave completa no se envía ni se guarda nunca.',
      'La fecha de instalación del SO se reinicia en las actualizaciones in-place de Windows: sirve para saber cuándo se instaló este Windows. Para la antigüedad del hardware se usa la fecha de BIOS.',
      'Los equipos con agente muestran la MAC, la IP principal y el número de serie bloqueados (candado): los manda el agente y el siguiente escaneo los revertiría. Para añadir otra IP se usa la tarjeta IPAM de la pestaña Red.',
      'Todo esto aparece en la tarjeta «Sistema y antigüedad» de la pestaña General de la ficha del ordenador.'
    ],
    checklist: [
      'El equipo tiene agente y ha escaneado al menos una vez.',
      'La tarjeta Sistema y antigüedad muestra fecha de BIOS e instalación del SO.',
      'La licencia muestra canal, clave parcial y estado.'
    ]
  }
];

function CodeBlock({ block }) {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(block.code);
    } catch (_) {
      // no-op: clipboard can fail on insecure contexts
    }
  };

  return (
    <div className="manual-code-wrap">
      <div className="manual-code-head">
        <span>{block.label}</span>
        <button type="button" className="manual-copy-btn" onClick={handleCopy}>
          Copiar
        </button>
      </div>
      <pre className="manual-code-block">
        <code>{block.code}</code>
      </pre>
    </div>
  );
}

export default function ManualCMDB({ companyId }) {
  const [activeId, setActiveId] = useState(MANUAL_SECTIONS[0].id);

  const activeSection = useMemo(
    () => MANUAL_SECTIONS.find((section) => section.id === activeId) || MANUAL_SECTIONS[0],
    [activeId]
  );

  return (
    <div className="manual-page">
      <aside className="manual-sidebar">
        <div className="manual-sidebar-title">Manual CMDB</div>
        <div className="manual-sidebar-subtitle">Empresa activa: {companyId || 'N/A'}</div>
        <nav className="manual-nav">
          {MANUAL_SECTIONS.map((section) => (
            <button
              key={section.id}
              type="button"
              className={`manual-nav-item ${activeId === section.id ? 'active' : ''}`}
              onClick={() => setActiveId(section.id)}
            >
              <span className="manual-nav-chapter">{section.chapter}</span>
              <span className="manual-nav-title">{section.title}</span>
            </button>
          ))}
        </nav>
      </aside>

      <main className="manual-content">
        <header className="manual-header">
          <span className="manual-chapter-chip">{activeSection.chapter}</span>
          <h1>{activeSection.title}</h1>
          <p>
            Manual operativo oficial de CMDB. Esta seccion se mantiene como referencia canonicamente soportada.
          </p>
        </header>

        <section className="manual-section">
          {activeSection.body?.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}

          {activeSection.checklist?.length ? (
            <ul className="manual-checklist">
              {activeSection.checklist.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          ) : null}

          {activeSection.codeBlocks?.map((block) => (
            <CodeBlock key={block.label} block={block} />
          ))}
        </section>
      </main>
    </div>
  );
}
